import { useEffect, useState } from 'react';
import { Button, Col, Form, GetProp, Input, Row, Switch, Upload, UploadProps } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onGetCountry, onUpdateCountry } from 'modules/countries/services/countries';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { onGetLanguage, onUpdateLanguage } from 'modules/languages/services/languages';
import { useTranslation } from 'react-i18next';

type FieldType = {
	name: string;
	code: string;
	flag: any;
	active: 0 | 1;
	default: 0 | 1;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function UpdateLanguagePage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [updateForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	useEffect(() => {
		getLanguage();
	}, []);

	const getLanguage = async () => {
		try {
			const response = await onGetLanguage(id as string);
			const language = response.data.data;

			updateForm.setFieldsValue({ name: language.name });
			updateForm.setFieldsValue({ code: language.code });
			updateForm.setFieldsValue({ active: language.active });
			updateForm.setFieldsValue({ default: language.default });
			setImageUrl(language.flag_url);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('LANGUAGES.FETCH_ERROR'));
			navigate('/languages');
		}
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateLanguagePayload = new FormData();
			updateLanguagePayload.append('_method', 'put');
			updateLanguagePayload.append('name', values.name);
			updateLanguagePayload.append('code', values.code);
			updateLanguagePayload.append('active', values.active ? '1' : '0');
			updateLanguagePayload.append('default', values.default ? '1' : '0');

			if (values.flag) updateLanguagePayload.append('flag', values.flag);

			await onUpdateLanguage(id as string, updateLanguagePayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('LANGUAGES.UPDATED_SUCCESS'));
			navigate('/languages');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('LANGUAGES.UPDATE_ERROR'));
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		updateForm.setFieldsValue({ flag: info.file });
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	const onDefaultChange = async (checked: boolean) => {
		updateForm.setFieldValue('default', checked ? 1 : 0);
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('LANGUAGES.UPDATE_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('LANGUAGES.DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<h6>{t('LANGUAGES.INFORMATION')}</h6>
								<Form.Item<FieldType> name="name" rules={[{ required: true, message: t('LANGUAGES.VALIDATION.NAME') }]}>
									<Input size="large" placeholder={t('LANGUAGES.PLACEHOLDER.NAME')} style={inputStyles} />
								</Form.Item>

								<Form.Item<FieldType> name="code" rules={[{ required: true, message: t('LANGUAGES.VALIDATION.CODE') }]}>
									<Input size="large" placeholder={t('LANGUAGES.PLACEHOLDER.CODE')} style={inputStyles} />
								</Form.Item>

								<div className="d-flex gap-2 py-3">
									<Form.Item<FieldType> name="active" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>

								<div className="d-flex gap-2 py-3">
									<Form.Item<FieldType> name="default" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('default')} onChange={onDefaultChange} />
									<p className="mb-0 fw-medium">{t('LANGUAGES.DEFAULT')}</p>
								</div>
							</Col>

							<Col span={24} md={12}>
								<div>
									<h6>{t('LANGUAGES.FLAG')}</h6>
									<Form.Item<FieldType> name="flag" hidden>
										<Input />
									</Form.Item>
									<Upload
										className="squareInput"
										listType="picture-card"
										showUploadList={false}
										beforeUpload={beforeUpload}
										onChange={handleChange}>
										{imageUrl ? (
											<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
										) : (
											<button style={{ border: 0, background: 'none' }} type="button">
												{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
												<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
											</button>
										)}
									</Upload>
								</div>
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							{t('COMMON.UPDATE')}
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateLanguagePage;
